import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { MOCK_TRIAGE_RESULTS } from "./mockTriageResults";
import type { TriageResultRow } from "./types";
import { TRIAGE_FLOW_STATUS_LABELS } from "./types";

export type TriageResultFilter = Readonly<{
  objectType: string;
  objectSubType: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}>;

export type TriageResultPageQuery = TriageResultFilter &
  Readonly<{
    page: number;
    pageSize: number;
  }>;

export type TriageResultPage = Readonly<{
  rows: TriageResultRow[];
  total: number;
}>;

function normalizeFilterValue(value: string): string {
  return value.trim().toLowerCase();
}

function isWithinDateRange(
  createDateIso: string,
  fromDate: Dayjs | null,
  toDate: Dayjs | null,
): boolean {
  if (!fromDate && !toDate) return true;
  const createDate = dayjs(createDateIso);
  if (!createDate.isValid()) return false;
  if (fromDate && createDate.isBefore(fromDate.startOf("day"))) return false;
  if (toDate && createDate.isAfter(toDate.endOf("day"))) return false;
  return true;
}

export function filterTriageResults(
  rows: TriageResultRow[],
  filter: TriageResultFilter,
): TriageResultRow[] {
  const objectType = normalizeFilterValue(filter.objectType);
  const objectSubType = normalizeFilterValue(filter.objectSubType);

  return rows.filter((row) => {
    if (objectType && normalizeFilterValue(row.objectType) !== objectType) {
      return false;
    }
    if (
      objectSubType &&
      normalizeFilterValue(row.objectSubType) !== objectSubType
    ) {
      return false;
    }
    if (!isWithinDateRange(row.createDate, filter.fromDate, filter.toDate)) {
      return false;
    }
    return true;
  });
}

export function listTriageResults(filter: TriageResultFilter): TriageResultRow[] {
  return filterTriageResults(MOCK_TRIAGE_RESULTS, filter);
}

export function fetchTriageResultPage(
  query: TriageResultPageQuery,
): TriageResultPage {
  const filtered = listTriageResults(query);
  const page = Math.max(1, query.page);
  const pageSize = Math.max(1, query.pageSize);
  const start = (page - 1) * pageSize;
  return {
    rows: filtered.slice(start, start + pageSize),
    total: filtered.length,
  };
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatCreateDateForExport(createDateIso: string): string {
  const parsed = dayjs(createDateIso);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : "-";
}

export function triageResultsToCsv(rows: TriageResultRow[]): string {
  const header = [
    "Result ID",
    "Object Type",
    "Object Sub-Type",
    "Object ID",
    "Title",
    "Version",
    "Triage Results",
    "Flow Status",
    "Create Date",
  ];
  const lines = rows.map((row) =>
    [
      row.resultId,
      row.objectTypeName,
      row.objectSubTypeName || "-",
      row.objectId,
      row.title,
      row.version,
      row.triageResult || "-",
      TRIAGE_FLOW_STATUS_LABELS[row.flowStatus],
      formatCreateDateForExport(row.createDate),
    ]
      .map(escapeCsvCell)
      .join(","),
  );
  return [header.join(","), ...lines].join("\n");
}

export function downloadTriageResultsCsv(rows: TriageResultRow[]): void {
  const csv = triageResultsToCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const dateStr = new Date().toISOString().slice(0, 10);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `triage-results-export-${dateStr}.csv`;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
